import { ROLE } from '@datacloud-incubation/frontend-ai-chat-module';
import z from 'zod';
import { paginationSchema } from '@core/schemas/pagination.schema';
import {
  FLOW_CHAT_ANSWER_STATUS,
  FLOW_CHAT_EVENTS,
  SOURCE_TYPE
} from '../constants/flowChat.constant';

const proposalSchema = z.object({
  id: z.string(),
  content: z.string(),
  children: z.object({
    sourceType: z.enum([SOURCE_TYPE.predefined, SOURCE_TYPE.none]),
    get source() {
      return z.array(proposalSchema).optional();
    }
  })
});

const sessionSchema = z.object({
  id: z.string(),
  contextId: z.string(),
  name: z.string(),
  greeting: z.string().nullish(),
  createdAt: z.string(),
  updatedAt: z.string().nullish(),
  isVisible: z.boolean(),
  isModifiedName: z.boolean(),
  proposals: z.array(proposalSchema).optional()
});

const chatMessageSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  content: z.string(),
  role: z.enum(ROLE),
  createdAt: z.string(),
  liked: z.boolean().optional(),
  disliked: z.boolean().optional(),
  dislikeMessage: z.string().nullish().optional(),
  responseTime: z.number().optional()
});

const sessionsListSchema = paginationSchema.extend({
  sessions: z.array(sessionSchema)
});

const createSessionSchema = z.object({
  session: sessionSchema
});

const getSessionSchema = z.object({
  session: sessionSchema
});

const createShareSessionSchema = z.object({
  share: z.object({
    id: z.uuidv4(),
    sessionId: z.string(),
    lastMessageId: z.string(),
    createdAt: z.iso.datetime()
  })
});

const chatHistorySchema = paginationSchema.extend({
  messages: z.array(chatMessageSchema),
  answerStatus: z.enum(FLOW_CHAT_ANSWER_STATUS)
});

const messageStreamResponseSchema = z.discriminatedUnion('event', [
  z.object({
    event: z.literal(FLOW_CHAT_EVENTS.messageStart),
    data: z.object({
      messageId: z.string()
    })
  }),
  z.object({
    event: z.literal(FLOW_CHAT_EVENTS.titleStart),
    data: z.object({
      sessionId: z.string()
    })
  }),
  z.object({
    event: z.literal(FLOW_CHAT_EVENTS.titleDelta),
    data: z.object({
      content: z.string()
    })
  }),
  z.object({
    event: z.literal(FLOW_CHAT_EVENTS.titleStop),
    data: z.object({}).refine((schema) => Object.keys(schema).length === 0, {
      message: 'Object schema is required be empty'
    })
  }),
  z.object({
    event: z.literal(FLOW_CHAT_EVENTS.messageDelta),
    data: z.object({
      content: z.string()
    })
  }),
  z.object({
    event: z.literal(FLOW_CHAT_EVENTS.messageStop),
    data: z.object({
      responseTime: z.number(),
      createdAt: z.string()
    })
  }),
  z.object({
    event: z.literal(FLOW_CHAT_EVENTS.complete),
    data: z.object({}).refine((schema) => Object.keys(schema).length === 0, {
      message: 'Object schema is required be empty'
    })
  }),
  z.object({
    event: z.literal(FLOW_CHAT_EVENTS.error),
    data: z.object({
      message: z.string(),
      error: z.string()
    })
  })
]);

export {
  sessionsListSchema,
  createSessionSchema,
  sessionSchema,
  chatMessageSchema,
  chatHistorySchema,
  messageStreamResponseSchema,
  proposalSchema,
  getSessionSchema,
  createShareSessionSchema
};
